/**
 * Watchlist state
 */
import { IWallet, enums } from '@kattana-io/common';
import {
  REMOVE_WALLET_SUCCESS,
  UPDATE_WALLET_SUCCESS,
  SET_TAB,
  SET_CHAIN,
  SET_WATCHED_WALLETS,
  SET_TOP_WALLETS,
  SET_BIG_TRADES_WALLETS,
  SET_WALLETS,
  SET_LOADING,
  SET_ACTIVE_WALLETS,
} from '../actions/wallet_watchlist';
import { UpdateWatchlistItemPayload } from './watchlist';

export const LS_WALLET_WATCHLIST_KEY = 'kattana.wallet_watchlist';
export const LS_WALLET_WATCHLIST_TAB_KEY = 'kattana.wallet_watchlist_tab';

export enum WalletWatchlistTab {
  Star = 'Favourites',
  TopWallets = 'Top',
  BigTradesWallets = 'Big Trades',
  ActiveWallets = 'Active',
}

export interface IWatchedWallet {
  address: string;
  wallet_id: number;
  chain: string;
  nickname: string;
  order: number;
  tag: enums.Tag;
  balance_usd: number;
}

export interface IBigTradesWallet {
  chain: string;
  address: string;
  value_usd: number;
  nickname: string;
}

export interface IBigVolumeWallet {
  chain: string;
  nickname: string;
  trades_amount: number;
  volume_24: number;
  address: string;
}

export type WatchlistWallet =
  | IWallet
  | IWatchedWallet
  | IBigTradesWallet
  | IBigVolumeWallet;

export interface IWalletWatchlistState {
  wallets: Array<WatchlistWallet>;
  tab: WalletWatchlistTab;
  chain: string | null;
  watchedWallets: IWatchedWallet[];
  topWallets: IWallet[];
  bigTradesWallets: IBigTradesWallet[];
  activeWallets: IBigVolumeWallet[];
  loading: boolean;
}

export const getDefaultState = (): IWalletWatchlistState => {
  const savedTab = window.localStorage?.getItem(LS_WALLET_WATCHLIST_TAB_KEY);
  return {
    wallets: [],
    tab: savedTab
      ? (savedTab as WalletWatchlistTab)
      : WalletWatchlistTab.TopWallets,
    chain: null,
    watchedWallets: [],
    topWallets: [],
    bigTradesWallets: [],
    activeWallets: [],
    loading: false,
  };
};

const defaultState = getDefaultState();

const removeWalletSuccess = (
  state: IWalletWatchlistState,
  payload: number
): IWalletWatchlistState => {
  const watchedWallets = state.watchedWallets.filter(
    (item) => item.wallet_id !== payload
  );
  if (state.tab === WalletWatchlistTab.Star) {
    return {
      ...state,
      watchedWallets,
      wallets: state.watchedWallets.filter(
        (item) => item.wallet_id !== payload
      ),
    };
  }
  return { ...state, watchedWallets };
};

const updateWalletSuccess = (
  state: IWalletWatchlistState,
  { id, payload }: { id: number; payload: UpdateWatchlistItemPayload }
): IWalletWatchlistState => {
  let watchlistItems = [...state.watchedWallets];
  if ('tag' in payload) {
    watchlistItems = watchlistItems.map((item) =>
      item.wallet_id === id ? { ...item, tag: payload.tag } : item
    );
  }
  if ('new' in payload && 'old' in payload) {
    const [removed] = watchlistItems.splice(payload.old, 1);
    watchlistItems.splice(payload.new, 0, removed);
    watchlistItems = watchlistItems.map((item, idx) => ({
      ...item,
      order: idx,
    }));
  }
  return {
    ...state,
    watchedWallets: watchlistItems,
  };
};

const setWallets = (
  state: IWalletWatchlistState,
  payload: Array<WatchlistWallet>
): IWalletWatchlistState => {
  if (state.tab === WalletWatchlistTab.Star) {
    return {
      ...state,
      wallets: state.watchedWallets,
    };
  }
  return {
    ...state,
    wallets: payload.map((item) => {
      const watchedItem = state.watchedWallets.find(
        (watchedWallet) =>
          item.address === watchedWallet.address &&
          item.chain === watchedWallet.chain
      );
      if (watchedItem) {
        return { ...item, tag: watchedItem.tag, order: watchedItem.order };
      }
      return item;
    }),
  };
};

const watchListReducer = (
  state: IWalletWatchlistState = defaultState,
  action: any
): IWalletWatchlistState => {
  switch (action.type) {
  case REMOVE_WALLET_SUCCESS:
    return removeWalletSuccess(state, action.payload);
  case UPDATE_WALLET_SUCCESS:
    return updateWalletSuccess(state, action.payload);
  case SET_TAB:
    return {
      ...state,
      tab: action.payload,
    };
  case SET_CHAIN:
    return {
      ...state,
      chain: action.payload,
    };
  case SET_WATCHED_WALLETS:
    return {
      ...state,
      watchedWallets: action.payload,
    };
  case SET_TOP_WALLETS:
    return {
      ...state,
      topWallets: action.payload,
    };
  case SET_BIG_TRADES_WALLETS:
    return {
      ...state,
      bigTradesWallets: action.payload,
    };
  case SET_ACTIVE_WALLETS:
    return {
      ...state,
      activeWallets: action.payload,
    };
  case SET_WALLETS:
    return setWallets(state, action.payload);
  case SET_LOADING:
    return {
      ...state,
      loading: action.payload,
    };
  default:
    return state;
  }
};

export default watchListReducer;
